import { css } from '@emotion/react';
import React from 'react';
import {
  GradientColorToken,
  GradientDirection,
  gradientDirectionMap,
  MarginProps,
  Responsive,
  ResponsiveHidingProps,
  ResponsivePaddingProps,
  useFullTheme,
} from '~/design';
import { BackgroundProvider } from '~/internal/BackgroundContext';
import { useGradient } from '~/utils';
import {
  useResponsivePaddingStyle,
  useResponsiveHideStyle,
  useResponsive,
  useMarginStyle,
} from '~/utils/styling';

export interface BoxProps extends ResponsivePaddingProps, ResponsiveHidingProps, MarginProps {
  children: React.ReactNode;

  /** The emphasis of the box against its background (default: `transparent`) */
  emphasis?: 'highlighted' | 'transparent';

  /** Adds a predefined gradient as the card background color. For accessibility concerns, make sure
   * that the text you'll add to the card has sufficient contrast with the background. */
  gradient?: GradientColorToken;

  /** What will the angle of the gradient background be. */
  gradientDirection?: GradientDirection;

  width?: number | Responsive<number>;
}

/**
 * `Box` is a basic layout component that provides a fixed or responsive padding around your content.
 * It renders a `div` element.
 */
export const Box = React.forwardRef<HTMLDivElement, BoxProps>(function Box(
  {
    children,
    emphasis = 'transparent',
    gradient,
    gradientDirection = 'horizontal',
    hideAbove,
    hideBelow,
    p,
    pb,
    pl,
    pr,
    pt,
    px,
    py,
    m,
    mb,
    ml,
    mr,
    mt,
    mx,
    my,
    width,
  },
  ref,
) {
  const paddingStyle = useResponsivePaddingStyle({
    p,
    pb,
    pl,
    pr,
    pt,
    px,
    py,
  });

  const marginStyle = useMarginStyle({
    m,
    mx,
    my,
    mt,
    mr,
    mb,
    ml,
  });

  const { colors } = useFullTheme();

  const hideStyle = useResponsiveHideStyle({ hideBelow, hideAbove });
  const gradientBg = useGradient(gradient);

  const backgroundStyle = css({
    background:
      emphasis === 'highlighted'
        ? colors.bgSecondaryHighlight
        : gradientBg
          ? `linear-gradient(${gradientDirectionMap[gradientDirection]}, ${gradientBg[0]}, ${gradientBg[1]})`
          : 'transparent',
  });

  const responsive = useResponsive();

  return (
    <BackgroundProvider backgroundColor={gradientBg}>
      <div
        css={[
          backgroundStyle,
          paddingStyle,
          marginStyle,
          hideStyle,
          width ? responsive.css({ width }) : {},
        ]}
        ref={ref}
      >
        {children}
      </div>
    </BackgroundProvider>
  );
});
